import { TableData } from 'models/ai-chat/visualization.types';
import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import * as XLSX from 'xlsx';
import { FaChevronDown, FaChevronUp, FaRegFileExcel, FaExpand, FaExpandArrowsAlt, FaCompressArrowsAlt, FaRegFilePdf } from 'react-icons/fa';
import { TableRequest } from 'models/ai-chat/table-pagination';
import TableFullViewModal from './TableFullView';
import { useChat } from 'store/useChatStore';
import { FilterItem } from 'models/ai-chat/filter-request';

interface TableViewProps {
  data: TableData;
  sqlQuery?: string;
  messageId?: string;
  onRemoveFilter?: (filter: any) => void;
  onClearAllFilters?: () => void;
  onDataUpdate?: (newData: TableData) => void;
  onFilterValueChange?: (filter: any, newValue: string) => void;
  onFilterUpdateComplete?: (updatedData: any) => void;
  filterData?: FilterItem[];
  customerId?: string;
  pdfGeneration?: boolean;
  onExportExcel?: () => void;
  onDownloadPDF?: () => void;
  onFullView?: () => void;
}

interface ColumnSize {
  index: number;
  width: number;
}

export interface TableViewRef {
  handleExport: () => void;
  handlePDFDownload: () => void;
  handleFullView: () => void;
}

const TableView = forwardRef<TableViewRef, TableViewProps>(({ 
  data, 
  sqlQuery, 
  messageId,
  onRemoveFilter, 
  onClearAllFilters,
  onDataUpdate,
  onFilterValueChange,
  onFilterUpdateComplete,
  filterData,
  customerId,
  pdfGeneration = false,
  onExportExcel,
  onDownloadPDF,
  onFullView
}, ref) => {

  
  if (!data || !data.headers || !data.rows) {
    return <div className="text-center p-4">No results found. Try adjusting your query or filters.</div>;
  }

  const [displayCount, setDisplayCount] = useState(10);
  const [sortConfig, setSortConfig] = useState<{ column: number; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<string[]>(Array(data?.headers?.length || 0).fill(''));
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [columnSizes, setColumnSizes] = useState<ColumnSize[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState<TableData>(data);
  const [reorderedData, setReorderedData] = useState<TableData | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const downloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [request] = useState<TableRequest>({
    sql_query : sqlQuery ,
    page_size: 50,
    page : 1
  });
  const tableRef = useRef<HTMLTableElement>(null);
  
  const { downloadTableAsPDF, isDownloadingPDF, pdfDownloadError, pdfDownloadErrorMessageId, clearPDFDownloadError } = useChat();

  // Safety check to ensure clearPDFDownloadError exists - memoized to prevent hook order issues
  const safeClearPDFDownloadError = useCallback(() => {
    if (clearPDFDownloadError) {
      clearPDFDownloadError();
    }
  }, [clearPDFDownloadError]);

  // Expose table functions to parent components
  useImperativeHandle(ref, () => ({
    handleExport,
    handlePDFDownload,
    handleFullView: () => setIsModalOpen(true)
  }), []);

  // Function to reorder columns - move image column to first position
  const reorderColumns = (data: TableData): TableData => {
    if (!data || !data.headers || !data.rows) return data;
    
    // Check if original data has an image column
    if (!hasOriginalImageColumn(data)) return data;
    
    // Find image column index in original data
    const imageColumnIndex = data.headers.findIndex((header, index) => {
      const headerStr = header.toString().toLowerCase();
      const imageKeywords = ['product image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
      return imageKeywords.some(keyword => headerStr.includes(keyword));
    });
    
    // If no image column found, return original data
    if (imageColumnIndex === -1) return data;
    
    // If image column is already first, return original data
    if (imageColumnIndex === 0) return data;
    
    // Create new headers array with image column first
    const newHeaders = [
      data.headers[imageColumnIndex], // Image column first
      ...data.headers.filter((_, index) => index !== imageColumnIndex) // Rest of columns
    ];
    
    // Create new rows array with reordered columns
    const newRows = data.rows.map(row => [
      row[imageColumnIndex], // Image column data first
      ...row.filter((_, index) => index !== imageColumnIndex) // Rest of column data
    ]);
    
    return {
      ...data,
      headers: newHeaders,
      rows: newRows
    };
  };

  useEffect(() => {
    if(data) {
      const reordered = reorderColumns(data);
      setCurrentData(reordered);
      setReorderedData(reordered);
      safeClearPDFDownloadError();
    }
  }, [data, safeClearPDFDownloadError]);

  useEffect(() => {
    if (currentData?.headers?.length !== filters.length) {
      setFilters(Array(currentData?.headers?.length || 0).fill(''));
    }
  }, [currentData?.headers?.length, filters.length]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (downloadTimeoutRef.current) {
        clearTimeout(downloadTimeoutRef.current);
      }
    };
  }, []);

  const handleDataUpdate = (newData: TableData) => {
    const reordered = reorderColumns(newData);
    setCurrentData(reordered);
    setReorderedData(reordered);
    if (onDataUpdate) {
      onDataUpdate(reordered);
    }
  };



  const isTimestampColumn = (columnIndex: number) => {
    if (!currentData.headers[columnIndex]) return false;
    const header = currentData.headers[columnIndex].toString().toLowerCase();
    const timestampKeywords = ['time', 'date', 'created', 'updated', 'modified', 'timestamp', 'executed'];
    return timestampKeywords.some(keyword => header.includes(keyword));
  };

  const isProductImageColumn = (columnIndex: number) => {
    if (!currentData.headers[columnIndex]) return false;
    const header = currentData.headers[columnIndex].toString().toLowerCase();
    const imageKeywords = ['product image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
    return imageKeywords.some(keyword => header.includes(keyword));
  };

  // Check if original data had an image column (for reordering logic)
  const hasOriginalImageColumn = (data: TableData) => {
    if (!data || !data.headers) return false;
    return data.headers.some((header, index) => {
      const headerStr = header.toString().toLowerCase();
      const imageKeywords = ['product image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
      return imageKeywords.some(keyword => headerStr.includes(keyword));
    });
  };

  const isImageUrl = (value: any) => {
    if (!value || typeof value !== 'string') return false;
    const url = value.toLowerCase().trim();
    return (
      url.startsWith('http://') || 
      url.startsWith('https://') || 
      url.startsWith('//')
    ) && (
      url.includes('.jpg') || 
      url.includes('.jpeg') || 
      url.includes('.png') || 
      url.includes('.gif') || 
      url.includes('.webp') || 
      url.includes('.svg')
    );
  };

  const isNumericColumn = (columnIndex: number) => {
    if (!currentData.headers[columnIndex]) return false;
    
    const header = currentData.headers[columnIndex].toString().toLowerCase();
    
    if (/\bid\b/.test(header)) return false;
    if (isTimestampColumn(columnIndex)) return false;
    if (header.includes('code') || header.includes('currency')) return false;
    if (header.includes('type') || header.includes('group')) return false;
    if (header.includes('number') || header.includes('account')) return false;
    if (header.includes('name') || header.includes('description')) return false;
    if (header.includes('status') || header.includes('category')) return false;
    
    const numericKeywords = ['amount', 'total', 'revenue', 'profit', 'loss', 'cost', 'price', 'value', 'balance', 'sum', 'count', 'percentage', 'percent', 'rate', 'fee', 'tax', 'income', 'expense'];
    if (numericKeywords.some(keyword => header.includes(keyword))) {
      return true;
    }

    const sampleSize = Math.min(20, currentData.rows.length);
    const sampleValues = currentData.rows.slice(0, sampleSize).map(row => row[columnIndex]);
    
    let numericCount = 0;
    let validValueCount = 0;
    
    sampleValues.forEach(val => {
      const strVal = String(val).trim().toLowerCase();
      
      if (strVal === '' || strVal === 'none' || strVal === 'null') {
        return;
      }
      
      validValueCount++;
      
      const cleanedVal = strVal.replace(/[,$%\s]/g, '');
      
      if (!isNaN(Number(cleanedVal)) && cleanedVal !== '') {
        const num = Number(cleanedVal);
        if (Number.isInteger(num) && Math.abs(num) > 999999999) {
          return;
        }
        numericCount++;
      }
    });
    
    return validValueCount >= 3 && (numericCount / validValueCount) >= 0.8;
  };

  const formatCellValue = (cell: any, columnIndex?: number) => {
    const value = String(cell).toLowerCase();
    
    if (value === "none" || value === "null") {
      return "";
    }
    
    if (columnIndex !== undefined && isTimestampColumn(columnIndex)) {
      const cellStr = String(cell).trim();
      
      const isDate = (str: string) => {
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}$/,
          /^\d{4}-\d{2}-\d{2}\s\d{1,2}:\d{2}:\d{2}$/,
          /^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}:\d{2}/,
          /^\d{1,2}\/\d{1,2}\/\d{4}/,
          /^\d{1,2}-\d{1,2}-\d{4}/,
          /^\d{4}\/\d{1,2}\/\d{1,2}/,
        ];
        
        return datePatterns.some(pattern => pattern.test(str));
      };
      
      if (isDate(cellStr)) {
        try {
          let date: Date;
          
          if (/^\d{4}-\d{2}-\d{2}$/.test(cellStr)) {
            const [year, month, day] = cellStr.split('-').map(Number);
            date = new Date(year, month - 1, day, 0, 0, 0);
          } else {
            date = new Date(cellStr);
          }
          
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          }
        } catch {
          return cell;
        }
      }
    }
    
    return cell;
  };

  const renderCellContent = (cell: any, columnIndex?: number) => {
    const formattedValue = formatCellValue(cell, columnIndex);
    
    // Check if this is a product image column and the value is an image URL
    if (columnIndex !== undefined && isProductImageColumn(columnIndex) && isImageUrl(cell)) {
      return (
        <div className="flex items-center justify-center">
          <img 
            src={String(cell).trim()} 
            alt="Product" 
            className="w-12 h-12 object-cover rounded border"
            onError={(e) => {
              // Fallback to text if image fails to load
              const img = e.currentTarget as HTMLImageElement;
              const nextSibling = img.nextElementSibling as HTMLElement;
              if (img && nextSibling) {
                img.style.display = 'none';
                nextSibling.style.display = 'block';
              }
            }}
          />
          <span style={{ display: 'none' }} className="text-xs text-gray-500 truncate">
            {formattedValue}
          </span>
        </div>
      );
    }
    
    return (
      <div className="truncate" title={String(formattedValue)}>
        {formattedValue}
      </div>
    );
  };

  const getColumnWidths = () => {
    const columnWidths = currentData.headers.map((header, index) => {
      // Set fixed width for image columns
      if (isProductImageColumn(index)) {
        return 80; // 80px for image columns
      }
      return String(header).length;
    });
    
    currentData.rows.forEach(row => {
      row.forEach((cell, index) => {
        if (index < columnWidths.length) {
          if (isProductImageColumn(index)) {
            // Keep fixed width for image columns
            return;
          }
          const cellLength = String(formatCellValue(cell, index)).length;
          columnWidths[index] = Math.max(columnWidths[index], cellLength);
        }
      });
    });
    
    const charWidth = 9;
    return columnWidths.map((width, index) => {
      if (isProductImageColumn(index)) {
        return 80; // Fixed width for image columns
      }
      return width * charWidth + 16;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    if (currentData?.headers) {
      const initialSizes = getColumnWidths().map((width, index) => ({
        index,
        width
      }));
      setColumnSizes(initialSizes);
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentData]);

  useEffect(() => {
    let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
    let mouseUpHandler: (() => void) | null = null;
    
    if (resizingColumn !== null) {
      mouseMoveHandler = (e: MouseEvent) => {
        handleResizeMove(e as unknown as React.MouseEvent);
      };
      
      mouseUpHandler = () => {
        handleResizeEnd();
      };
      
      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
    }
    
    return () => {
      if (mouseMoveHandler) {
        document.removeEventListener('mousemove', mouseMoveHandler);
      }
      if (mouseUpHandler) {
        document.removeEventListener('mouseup', mouseUpHandler);
      }
    };
  }, [resizingColumn, startX]);

  const handleExport = useCallback(() => {
    safeClearPDFDownloadError();
    
    // Generate a readable filename with datetime
    const now = new Date();
    const dateTime = now.toISOString()
      .replace(/:/g, '-')  // Replace colons with dashes for filename compatibility
      .replace(/\..+/, '') // Remove milliseconds
      .replace('T', '_');  // Replace T with underscore
    
    // Create a descriptive filename
    const fileName = `Table_Data_${dateTime}.xlsx`;
    
    const worksheet = XLSX.utils.aoa_to_sheet([currentData.headers, ...currentData.rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, fileName);
  }, [currentData, safeClearPDFDownloadError]);

  const handleShowMore = () => {
    safeClearPDFDownloadError();
    setDisplayCount(prev => Math.min(prev + 10, filteredAndSortedRows.length));
  };

  const handleShowLess = () => {
    safeClearPDFDownloadError();
    setDisplayCount(10);
  };

  const handleSort = (columnIndex: number) => {
    safeClearPDFDownloadError();
    
    setSortConfig(prev => {
      if (prev?.column === columnIndex) {
        return { column: columnIndex, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnIndex, direction: 'asc' };
    });
  };

  const handleFilterChange = (index: number, value: string) => {
    safeClearPDFDownloadError();
    
    const newFilters = [...filters];
    newFilters[index] = value;
    setFilters(newFilters);
    setDisplayCount(10); 
  };

  const handleResizeStart = (e: React.MouseEvent, columnIndex: number) => {
    e.preventDefault();
    setResizingColumn(columnIndex);
    setStartX(e.clientX);
  };

  const handleResizeMove = (e: React.MouseEvent) => {
    if (resizingColumn === null) return;

    const deltaX = e.clientX - startX;
    if (Math.abs(deltaX) < 5) return; 

    setStartX(e.clientX);
    setColumnSizes(prev => {
      const newSizes = [...prev];
      const columnToResize = newSizes.find(col => col.index === resizingColumn);
      
      if (columnToResize) {
        const newWidth = Math.max(50, columnToResize.width + deltaX);
        columnToResize.width = newWidth;
      }
      
      return newSizes;
    });
  };

  const handleResizeEnd = () => {
    setResizingColumn(null);
  };

   const handlePDFDownload = useCallback(async () => {
    if (!messageId || isDownloading || isDownloadingPDF) {
      console.log('PDF Download blocked - messageId:', messageId, 'isDownloading:', isDownloading, 'isDownloadingPDF:', isDownloadingPDF);
      return;
    }

    // Clear any existing timeout to prevent double calls
    if (downloadTimeoutRef.current) {
      clearTimeout(downloadTimeoutRef.current);
    }

    // Debounce the download call
    downloadTimeoutRef.current = setTimeout(async () => {
      setIsDownloading(true);
      safeClearPDFDownloadError();

      try {
        // Get the latest filter data - use prop if available, otherwise empty array
        const latestFilterData = filterData || [];
        
        // Extract date range from filterData
        let startDate: string | undefined;
        let toDate: string | undefined;
        
        if (latestFilterData && latestFilterData.length > 0) {
          latestFilterData.forEach(filter => {
            if (filter.columnName && filter.columnName.toLowerCase().includes('date')) {
              if (filter.operator === '>=' || filter.operator === '>') {
                startDate = filter.columnValue;
              } else if (filter.operator === '<=' || filter.operator === '<') {
                toDate = filter.columnValue;
              }
            }
          });
        }

        console.log('PDF Download - messageId:', messageId, 'startDate:', startDate, 'toDate:', toDate, 'filterData:', latestFilterData, 'filterDataLength:', latestFilterData?.length);
        await downloadTableAsPDF(messageId, startDate, toDate, latestFilterData);
      } finally {
        setIsDownloading(false);
      }
    }, 100); // Small delay to prevent double calls
  }, [messageId, filterData, downloadTableAsPDF, safeClearPDFDownloadError, isDownloading, isDownloadingPDF]);

  let filteredAndSortedRows = currentData?.rows.filter(row =>
    row.every((cell, i) =>
      String(formatCellValue(cell, i)).toLowerCase().includes(filters[i]?.toLowerCase() || '')
    )
  );

  if (sortConfig) {
    const { column, direction } = sortConfig;
    filteredAndSortedRows.sort((a, b) => {
      const valA = a[column];
      const valB = b[column];
      
      // Check for empty values
      const isEmptyA = valA === null || valA === undefined || valA === '' || 
                       String(valA).toLowerCase() === 'null';
      const isEmptyB = valB === null || valB === undefined || valB === '' || 
                       String(valB).toLowerCase() === 'null';
      
      // For numeric columns: empty values always go to bottom
      if (isNumericColumn(column)) {
        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA) return 1;  // A goes to bottom
        if (isEmptyB) return -1; // B goes to bottom
        
        // Parse as numbers for proper numeric sorting
        const numA = parseFloat(String(valA).replace(/[,$%\s]/g, ''));
        const numB = parseFloat(String(valB).replace(/[,$%\s]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return direction === 'asc' ? numA - numB : numB - numA;
        }
      }
      
      // For non-numeric columns: use string comparison
      const formattedA = String(formatCellValue(valA, column)).toLowerCase();
      const formattedB = String(formatCellValue(valB, column)).toLowerCase();
      
      if (formattedA < formattedB) return direction === 'asc' ? -1 : 1;
      if (formattedA > formattedB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const displayedRows = filteredAndSortedRows.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedRows.length;

  const totalTableWidth = columnSizes.reduce((sum, col) => sum + col.width, 0);
  const mightNeedScrolling = totalTableWidth > (isMobile ? window.innerWidth - 40 : 800);

  const renderActionButtons = () => (
    <div className="flex justify-end items-center space-x-2 mt-3">
      {displayCount > 10 && (
        <button 
          onClick={handleShowLess} 
          className="flex items-center px-2 py-1 text-xs text-tertiary-text hover:bg-[#e6e6e6] rounded-full transition-colors" 
        >
          Show less
          <FaChevronUp className="ml-1.5 w-3 h-3" />
        </button>
      )}
      
      {hasMore && (
        <button
          onClick={handleShowMore}
          className="flex items-center px-2 py-1 text-xs text-tertiary-text hover:bg-[#e6e6e6] rounded-full transition-colors"
        >
          Show more ({displayCount} of {filteredAndSortedRows.length})
          <FaChevronDown className="ml-1 w-3 h-3" />
        </button>
      )}
    </div>
  );

  const renderMobileView = () => (
    <div className="w-full">
      {displayedRows.map((row, rowIndex) => (
        <div 
          key={rowIndex}
          className={`border rounded-md mb-4 p-3 ${rowIndex % 2 === 0 ? 'bg-White' : 'bg-Mystic'} hover:bg-blue-50 transition-all duration-300 ease-in-out shadow-sm`}
        >
          {currentData.headers.map((header, cellIndex) => (
            <div key={cellIndex} className="flex py-1 border-b border-gray-100 last:border-b-0">
              <div className="font-medium text-gray-700 w-1/3">{header}:</div>
              <div className="w-2/3">{renderCellContent(row[cellIndex], cellIndex)}</div>
            </div>
          ))}
        </div>
      ))}
      
      <div className="w-full flex justify-end mt-2">
        {renderActionButtons()}
      </div>
      
      {pdfDownloadError && pdfDownloadErrorMessageId === messageId && (
        <div className="text-red-500 text-xs mt-1 text-center">
          {pdfDownloadError}
        </div>
      )}
    </div>
  );

  const resizeHandleStyle = {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    height: '100%',
    width: '5px',
    cursor: 'col-resize',
    userSelect: 'none' as const,
    backgroundColor: resizingColumn !== null ? 'var(--primary)' : 'transparent',
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    if (pdfDownloadError && pdfDownloadErrorMessageId === messageId) {
      timeoutId = setTimeout(() => {
        safeClearPDFDownloadError();
      }, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [pdfDownloadError, pdfDownloadErrorMessageId, messageId, safeClearPDFDownloadError]);

  return (
    <div className="w-full">
      <TableFullViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={currentData}
        sortConfig={sortConfig}
        onSort={handleSort}
        filteredAndSortedRows={filteredAndSortedRows}
        isNumericColumn={isNumericColumn}
        formatCellValue={formatCellValue}
        onExport={handleExport}
        request={request}
        onDataUpdate={handleDataUpdate}
        // AppliedFiltersDisplay props
        sqlQuery={sqlQuery}
        messageId={messageId}
        onFilterValueChange={onFilterValueChange}
        onFilterUpdateComplete={onFilterUpdateComplete}
        filterData={filterData}
        customerId={customerId}
        onExportExcel={onExportExcel}
        onDownloadPDF={onDownloadPDF}
      />

      {isMobile ? (
        renderMobileView()
      ) : (
        <div className={`${mightNeedScrolling ? 'w-full' : 'inline-block'} relative`}>

          
          {pdfDownloadError && pdfDownloadErrorMessageId === messageId && (
            <div className="text-red-500 text-xs mb-2 text-center">
              {pdfDownloadError}
            </div>
          )}

          <div className={`overflow-x-auto ${isFullScreen ? 'fixed inset-0 z-50 bg-white p-4' : ''}`} style={{ maxWidth: '100%' }}>
            <table
              ref={tableRef}
              className="border-collapse text-LimedSpruce rounded-md border-t border-b border-Botticelli relative"
              style={{ width: mightNeedScrolling ? totalTableWidth : 'auto' }}
            >
              <colgroup>
                {columnSizes.map((col) => (
                  <col key={col.index} style={{ width: `${col.width}px`, minWidth: `${col.width}px` }} />
                ))}
              </colgroup>
              <thead className="bg-WhiteLilac">
                <tr>
                  {currentData.headers.map((header, index) => (
                    <th
                      key={index}
                      onClick={() => handleSort(index)}
                      className={`px-3 py-1.5 text-ai80 text-gray80 font-semibold tracking-wider text-Cello border-b border-gray-200 cursor-pointer relative capitalize ${
                        isProductImageColumn(index) 
                          ? 'text-center' 
                          : isNumericColumn(index) 
                          ? 'text-right' 
                          : 'text-left'
                      }`}
                      style={{ fontWeight: 600, textTransform: 'capitalize', paddingTop: '0.625rem', paddingBottom: '0.625rem' }}
                    >
                      <div className="flex items-center space-x-1 truncate">
                        <span>{header}</span>
                        <span>
                          {sortConfig?.column === index
                            ? (sortConfig.direction === 'asc' ? '↑' : '↓')
                            : ''}
                        </span>
                      </div>
                      <div 
                        style={resizeHandleStyle}
                        onMouseDown={(e) => handleResizeStart(e, index)}
                        className="hover:bg-blue-200"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`${rowIndex % 2 === 0 ? 'bg-White' : 'bg-Mystic'} hover:bg-blue-50 transition-all duration-300 ease-in-out`}
                    style={{
                      opacity: rowIndex < displayCount ? 1 : 0,
                      transform: `translateY(${rowIndex < displayCount ? 0 : 20}px)`
                    }}
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={`px-3 text-ai80 text-gray80 border-b border-gray-100 ${
                          isProductImageColumn(cellIndex) 
                            ? 'text-center' 
                            : isNumericColumn(cellIndex) 
                            ? 'text-right' 
                            : 'text-left'
                        }`}
                        style={{ paddingTop: '0.625rem', paddingBottom: '0.625rem' }}
                      >
                        {renderCellContent(cell, cellIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            width: mightNeedScrolling ? '100%' : ((totalTableWidth + 45) + 'px'),
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '0.75rem'
          }}>
            {renderActionButtons()}
          </div>
        </div>
      )}
    </div>
  );
});

TableView.displayName = 'TableView';

export default TableView;